<template>
    <div class="pdf">

        <div class="control-panel">
            <div class="row">
                <label class="top">
                    <div class="switch">
                        <input type="checkbox" v-model="value.has_signatures" @change="render(); save()"
                               :disabled="state === 'saving'"/>
                        <div class="slider"></div>

                    </div>
                    <p>{{ $t('custom-contract.edit.label') }}</p>
                </label>
            </div>
            <div class="row" v-if="value.has_signatures">
                <label class="top">
                    <div class="switch">
                        <input type="checkbox" v-model="value.has_extra_tenant_signature" @change="render(); save()"
                               :disabled="state === 'saving'"/>
                        <div class="slider"></div>

                    </div>
                    <p>Has extra tenant signature</p>
                </label>
            </div>
        </div>

        <div class="canvas" ref="canvas">
            <Moveable id="landlordMoveableSignature"
                      v-if="value && displaySignatures && value.has_signatures"
                      class="moveable landlord"
                      @drag="handleDrag"
                      @resize="handleResize"
                      @dragEnd="setSignatureCoordinates"
                      @resizeEnd="setSignatureCoordinates"
                      v-bind="moveable"
                      :container="$refs.canvas"
                      ref="landlordSignature"
            >
                <span>{{ $t('custom-contract.signature.landlord') }}</span>
            </Moveable>

            <Moveable id="extraTenantMoveableSignature"
                      v-if="value && displaySignatures && value.has_signatures"
                      v-show="value.has_extra_tenant_signature"
                      class="moveable extra-tenant"
                      @drag="handleDrag"
                      @resize="handleResize"
                      @dragEnd="setSignatureCoordinates"
                      @resizeEnd="setSignatureCoordinates"
                      v-bind="moveable"
                      :container="$refs.canvas"
                      ref="extraTenantSignature"
            >
                <span>Extra<br>{{ $t('custom-contract.signature.tenant') }}</span>
            </Moveable>

            <Moveable id="tenantMoveableSignature"
                      v-if="value && displaySignatures && value.has_signatures"
                      class="moveable tenant"
                      @drag="handleDrag"
                      @resize="handleResize"
                      @dragEnd="setSignatureCoordinates"
                      @resizeEnd="setSignatureCoordinates"
                      v-bind="moveable"
                      :container="$refs.canvas"
                      ref="tenantSignature"
            >
                <span>{{ $t('custom-contract.signature.tenant') }}</span>
            </Moveable>
            <div v-if="state === 'saving'">
                <Loader></Loader>
            </div>
        </div>
        <div class="row footer">
            <button @click="reset()" class="button transparent" :disabled="state === 'saving'">
                {{ $t('custom-contract.signature.reset') }}
            </button>

            <button class="purple" @click="save()" :disabled="state === 'saving'">
                <span v-if="state === 'saving'">{{ $t('digital.approvecontract.loading') }}</span>
                <span v-else>{{ $t('custom-contract.signature.save-this-page') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import Moveable from "vue-moveable";
import {mapGetters} from "vuex";

import Loader from '../../../components/Loader.vue';

const SIGNATURE_RATIO = 2.39;
const SIGNATURE_WIDTH_RATIO = 0.33; // 25% of page width

export default {
    name: "CustomContractSignatureEditor",
    components: {
        Moveable,
        Loader
    },
    props: ['value', 'customContract', 'state'],
    data: () => ({
        displaySignatures: false,
        moveable: {
            dragArea: true,
            snappable: true,
            resizable: true,
            keepRatio: true,
            draggable: true,
            bounds: {left: 0, right: 0, top: 0, bottom: 0},
        },
        postData: {
            has_signatures: false,
            has_extra_tenant_signature: false,
            tenant_signature_top: 0,
            tenant_signature_left: 0,
            tenant_signature_width: 0,
            tenant_signature_height: 0,
            landlord_signature_top: 0,
            landlord_signature_left: 0,
            landlord_signature_width: 0,
            landlord_signature_height: 0,
            extra_tenant_signature_top: 0,
            extra_tenant_signature_left: 0,
            extra_tenant_signature_width: 0,
            extra_tenant_signature_height: 0,
            scale: 1
        },
        image: {},
    }),
    computed: {
        ...mapGetters(['getToken'])
    },
    mounted() {
        window.addEventListener('resize', () => {
            this.postData.scale = this.image.naturalHeight / this.$refs.canvas.clientHeight;
            this.render();
        });

        this.initialPostData = _.clone(this.postData);
    },
    methods: {

        // Moveable event handler for resizeable behavior

        handleResize({ target, width, height }) {
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
        },

        // Moveable event handler for draggable behavior

        handleDrag({target, transform, currentTarget, beforeDelta}) {

            // Get coordinates of moveable rectangle element

            function getCoordinates(moveable) {

                let rect = moveable.getRect();

                return {
                    left: rect.left,
                    top: rect.top,
                    right: rect.left + rect.width,
                    bottom: rect.top + rect.height
                }
            }

            // Restricted zone is passive rectangle

            if (currentTarget === this.$refs.tenantSignature.moveable) {
                var restrictedCoordinates = getCoordinates(this.$refs.landlordSignature.moveable)
            }

            if (currentTarget === this.$refs.extraTenantSignature.moveable) {
                var restrictedCoordinates = getCoordinates(this.$refs.landlordSignature.moveable)
            }

            if (currentTarget === this.$refs.landlordSignature.moveable) {
                var restrictedCoordinates = getCoordinates(this.$refs.tenantSignature.moveable)
            }

            // Target coordinates of active (moveable now) rectangle

            let targetCoordinates = getCoordinates(currentTarget);

            // Check if we don't intersect

            function intersectRect(r1, r2) {
                return !(r2.left > r1.right ||
                    r2.right < r1.left ||
                    r2.top > r1.bottom ||
                    r2.bottom < r1.top);
            }

            // Detect moving out of intersected elements

            function movesOut(r1, r2, d) {
                return (r2.left > r1.right + d[0] ||
                    r2.right < r1.left + d[0] ||
                    r2.top > r1.bottom + d[1] ||
                    r2.bottom < r1.top + d[1]);
            }

            // We lock elements if their intersect, but allow to move out active one

            if (intersectRect(targetCoordinates, restrictedCoordinates) && !movesOut(targetCoordinates, restrictedCoordinates, beforeDelta)) {
                return;
            }

            target.style.transform = transform;
        },

        // Convert values in pts from DB to pixels

        getPixelValue(ptKnownValue, pxKnownValue, ptUnknown) {
            return parseInt(ptUnknown * pxKnownValue / ptKnownValue)
        },

        // Set signature coordinates, event handler for Moveable

        setSignatureCoordinates({target, currentTarget}) {

            let x = currentTarget.getRect().left;
            let y = currentTarget.getRect().top;
            let w = currentTarget.getRect().width;
            let h = currentTarget.getRect().height;

            x = x < 0 ? 0 : x; // Sometimes Moveable forces negative values
            y = y < 0 ? 0 : y; // In this case force 0

            if (target.classList.contains('landlord')) {
                this.postData.landlord_signature_left = parseInt(x);
                this.postData.landlord_signature_top = parseInt(y);
                this.postData.landlord_signature_width = parseInt(w);
                this.postData.landlord_signature_height = parseInt(h);
            }

            if (target.classList.contains('tenant')) {
                this.postData.tenant_signature_left = parseInt(x);
                this.postData.tenant_signature_top = parseInt(y);
                this.postData.tenant_signature_width = parseInt(w);
                this.postData.tenant_signature_height = parseInt(h);
            }

            if (target.classList.contains('extra-tenant')) {
                this.postData.extra_tenant_signature_left = parseInt(x);
                this.postData.extra_tenant_signature_top = parseInt(y);
                this.postData.extra_tenant_signature_width = parseInt(w);
                this.postData.extra_tenant_signature_height = parseInt(h);
            }
        },

        // Draw page

        render() {
            // Draw canvas

            if (!this.image)
                return;

            const canvasHeight = this.$refs.canvas.clientHeight;
            const canvasWidth = this.image.naturalWidth / this.postData.scale;

            this.$refs.canvas.style.width = `${canvasWidth}px`
            this.$refs.canvas.style.backgroundImage = `url('${this.image.src}')`

            // Lock moveable signatures in bounds

            this.moveable.bounds.right = canvasWidth;
            this.moveable.bounds.bottom = canvasHeight;

            // Signature width and height depends of page width and height and constant aspect ratio

            this.displaySignatures = true;

            // Workaround

            this.$nextTick(() => {

                if (!this.$refs.tenantSignature || !this.$refs.landlordSignature || !this.$refs.extraTenantSignature)
                    return; // There is no rendered Moveable signatures yet

                // Update moveable signatures width and height after render

                let reset = (moveableComponent, signatureTop = 0, signatureLeft = 0, signatureWidth = 0, signatureHeight = 0, putOnLeft = true, isExtra = false) => {

                    if ((signatureTop === 0 && signatureLeft === 0) || (signatureWidth === 0 && signatureHeight === 0)) {

                        signatureWidth = parseInt(canvasWidth * SIGNATURE_WIDTH_RATIO);
                        signatureHeight = parseInt(signatureWidth / SIGNATURE_RATIO);

                        // New created pages have 0 as signature positions
                        let top = canvasHeight - signatureHeight - 20;
                        moveableComponent.$el.style.top = `${top}px`
                        moveableComponent.$el.style.width  = signatureWidth + 'px';
                        moveableComponent.$el.style.height = signatureHeight + 'px';

                        if (putOnLeft) { // Render signature on the left side of the document
                            moveableComponent.$el.style.left = `20px`
                        } else { // Or on the right
                            moveableComponent.$el.style.left = `${canvasWidth - signatureWidth - 20}px`
                        }

                        if(isExtra) {
                            moveableComponent.$el.style.top = `${top - signatureHeight - 20}px`
                        }
                    } else {
                        moveableComponent.$el.style.top = this.getPixelValue(this.value.height, canvasHeight, signatureTop) + 'px';
                        moveableComponent.$el.style.left = this.getPixelValue(this.value.width, canvasWidth, signatureLeft) + 'px';
                        moveableComponent.$el.style.width = this.getPixelValue(this.value.height, canvasHeight, signatureWidth) + 'px';
                        moveableComponent.$el.style.height = this.getPixelValue(this.value.width, canvasWidth, signatureHeight) + 'px';
                    }

                    moveableComponent.$el.style.transform = null;

                    moveableComponent.updateRect();

                    this.setSignatureCoordinates({
                        target: moveableComponent.$el,
                        currentTarget: moveableComponent.moveable
                    });
                }

                reset(this.$refs.tenantSignature,
                    this.value.tenant_signature_top,
                    this.value.tenant_signature_left,
                    this.value.tenant_signature_width,
                    this.value.tenant_signature_height,
                    false);

                reset(this.$refs.extraTenantSignature,
                    this.value.extra_tenant_signature_top,
                    this.value.extra_tenant_signature_left,
                    this.value.extra_tenant_signature_width,
                    this.value.extra_tenant_signature_height,
                    false,
                    true);

                reset(this.$refs.landlordSignature,
                    this.value.landlord_signature_top,
                    this.value.landlord_signature_left,
                    this.value.landlord_signature_width,
                    this.value.landlord_signature_height,
                    true);
            });
        },

        // Load and render page

        loadImage() {
            this.image = {};
            this.displaySignatures = false;

            new Promise(resolve => {
                const image = new Image();
                image.addEventListener('load', () => {
                    resolve(image);
                });
                image.src = `${this.value.image}?token=${this.getToken}`;
            }).then((image) => {
                this.image = image;
                this.postData.scale = this.image.naturalHeight / this.$refs.canvas.clientHeight;
                this.render();
            }).catch((e) => {
                this.$emit('error', e)
            });
        },

        // Put coordinates on server

        save() {
            this.postData.has_signatures = this.value.has_signatures;
            this.postData.has_extra_tenant_signature = this.value.has_extra_tenant_signature;
            this.$emit('saving');

            axios({
                method: 'PUT',
                url: `${window.config.apiUrl}api/custom-contract/${this.customContract.id}/page/${this.value.id}`,
                cancelToken: window.AuthorizationCancelSource.token,
                data: this.postData
            }).then(response => {
                Object.assign(this.value, response.data.data);
                this.$emit('input', this.value);
                this.$emit('save')
                this.$store.commit('setSuccessBarMessage', this.$t('successbar.label.page-saved'));
            }).catch((e) => {
                this.$emit('error', e)
            });
        },

        // Reset signature positions

        reset() {
            let customContractPage = this.value;

            customContractPage.landlord_signature_left = 0;
            customContractPage.landlord_signature_top = 0;
            customContractPage.tenant_signature_left = 0;
            customContractPage.tenant_signature_top = 0;
            customContractPage.extra_tenant_signature_left = 0;
            customContractPage.extra_tenant_signature_top = 0;

            Object.assign(this.value, customContractPage);

            this.$emit('input', customContractPage);

            this.render();
        }
    },
    watch: {
        value: {
            immediate: true,
            handler() {
                this.loadImage();
            }
        }
    }
}
</script>

<style lang="scss" scoped>


</style>
